"use client";

import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";

import { useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { RiCalendarLine, RiCheckLine, RiCloseLine } from "react-icons/ri";

import { useClinicClock } from "@/hooks/use-clinic-clock";
import {
  DateDirection,
  DateSuggestion,
  useSmartDateInput,
} from "@/hooks/use-smart-date-input";

import { type CalendarDay, dayMarker } from "@/lib/clinic-clock";
import { cx, focusInput } from "@/lib/tailwind-utils";

import Button from "./button";
import { ButtonGroup } from "./button-group";
import Input from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { SmartDatePickerPanel } from "./smart-date-picker-panel";

// Re-export types for backward compatibility (DateRange comes from react-day-picker,
// but date-picker.tsx already exports its own DateRange type)
export type { DateSuggestion, DateDirection };

// Range suggestion type
interface RangeSuggestion {
  id: string;
  label: string;
  range: DateRange;
  isRange: true;
  /** For open-ended ranges like "after" or "before" */
  rangeType?: "after" | "before" | "between";
}

// Special date markers for open-ended ranges (same as old component)
const FUTURE_DATE_MARKER = "9000-12-31";
const PAST_DATE_MARKER = "1970-01-01";

// Generate range suggestions based on direction
const generateRangeSuggestions = (
  direction: DateDirection,
  clinicToday: CalendarDay,
): RangeSuggestion[] => {
  const today = dayMarker(clinicToday);

  // Common "after" and "before" options
  const afterBeforeOptions: RangeSuggestion[] = [
    {
      id: "after-today",
      label: "After today",
      range: { from: today, to: new Date(FUTURE_DATE_MARKER) },
      isRange: true,
      rangeType: "after",
    },
    {
      id: "before-today",
      label: "Before today",
      range: { from: new Date(PAST_DATE_MARKER), to: today },
      isRange: true,
      rangeType: "before",
    },
  ];

  if (direction === "past") {
    return [
      {
        id: "last-7-days",
        label: "Last 7 days",
        range: { from: subDays(today, 6), to: today },
        isRange: true,
        rangeType: "between",
      },
      {
        id: "last-14-days",
        label: "Last 14 days",
        range: { from: subDays(today, 13), to: today },
        isRange: true,
        rangeType: "between",
      },
      {
        id: "last-30-days",
        label: "Last 30 days",
        range: { from: subDays(today, 29), to: today },
        isRange: true,
        rangeType: "between",
      },
      {
        id: "this-month",
        label: "This month",
        range: { from: startOfMonth(today), to: today },
        isRange: true,
        rangeType: "between",
      },
      {
        id: "last-month",
        label: "Last month",
        range: {
          from: startOfMonth(subMonths(today, 1)),
          to: endOfMonth(subMonths(today, 1)),
        },
        isRange: true,
        rangeType: "between",
      },
      ...afterBeforeOptions,
    ];
  } else {
    return [
      {
        id: "next-3-days",
        label: "Next 3 days",
        range: { from: today, to: addDays(today, 2) },
        isRange: true,
        rangeType: "between",
      },
      {
        id: "next-7-days",
        label: "Next 7 days",
        range: { from: today, to: addDays(today, 6) },
        isRange: true,
        rangeType: "between",
      },
      {
        id: "next-14-days",
        label: "Next 14 days",
        range: { from: today, to: addDays(today, 13) },
        isRange: true,
        rangeType: "between",
      },
      {
        id: "this-month",
        label: "This month",
        range: { from: today, to: endOfMonth(today) },
        isRange: true,
        rangeType: "between",
      },
      {
        id: "next-month",
        label: "Next month",
        range: {
          from: startOfMonth(addMonths(today, 1)),
          to: endOfMonth(addMonths(today, 1)),
        },
        isRange: true,
        rangeType: "between",
      },
    ];
  }
};

// Format range for display, handling open-ended ranges
const formatRangeDisplay = (range: DateRange, rangeType?: string): string => {
  if (!range.from && !range.to) return "";

  // Partial range (only one date selected so far)
  if (range.from && !range.to) return format(range.from, "MMM d, yyyy");
  if (!range.from && range.to) return format(range.to, "MMM d, yyyy");

  const fromStr = format(range.from!, "yyyy-MM-dd");
  const toStr = format(range.to!, "yyyy-MM-dd");

  // Check for open-ended ranges
  if (toStr === FUTURE_DATE_MARKER || rangeType === "after") {
    return `After ${format(range.from!, "MMM d, yyyy")}`;
  }
  if (fromStr === PAST_DATE_MARKER || rangeType === "before") {
    return `Before ${format(range.to!, "MMM d, yyyy")}`;
  }

  // Same date = exact date
  if (fromStr === toStr) {
    return format(range.from!, "MMM d, yyyy");
  }

  // Regular range
  return `${format(range.from!, "MMM d, yyyy")} – ${format(range.to!, "MMM d, yyyy")}`;
};

export type DateMode = "single" | "range";

interface SmartDateInputBaseProps {
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  side?: "top" | "bottom";
  customSuggestions?: DateSuggestion[];
  /** Direction for date suggestions: "future" (default) or "past" */
  direction?: DateDirection;
  /** Whether to auto-focus the input on mount */
  autoFocus?: boolean;
}

interface SmartDateInputSingleProps extends SmartDateInputBaseProps {
  mode?: "single";
  value?: Date;
  onChange?: (date: Date | undefined) => void;
}

interface SmartDateInputRangeProps extends SmartDateInputBaseProps {
  mode: "range";
  value?: DateRange;
  onChange?: (range: DateRange | undefined) => void;
}

type SmartDateInputProps = SmartDateInputSingleProps | SmartDateInputRangeProps;

export function SmartDateInput(props: SmartDateInputProps) {
  const {
    placeholder,
    className,
    disabled = false,
    side = "bottom",
    customSuggestions,
    direction = "future",
    autoFocus = false,
  } = props;

  const mode = props.mode ?? "single";
  const value = props.value;
  const onChange = props.onChange;

  // Generate appropriate placeholder based on direction and mode
  const getDefaultPlaceholder = () => {
    if (mode === "range") {
      return direction === "past"
        ? "Select date range..."
        : "Select date range...";
    }
    return direction === "past"
      ? "yesterday, 2 weeks ago, last month, mar 15..."
      : "today, tomorrow, next week, mar 15...";
  };
  const defaultPlaceholder = getDefaultPlaceholder();

  const [showDatePicker, setShowDatePicker] = useState(false);

  const clinicToday = useClinicClock().today();
  const rangeControlledInputDisplay = useMemo(() => {
    if (mode !== "range") return undefined;
    const rangeValue = value as DateRange | undefined;
    if (!rangeValue?.from && !rangeValue?.to) return "";
    // Check if the range matches a known suggestion and use its shorthand label
    const suggestions = generateRangeSuggestions(direction, clinicToday);
    const fromStr = rangeValue?.from
      ? format(rangeValue.from, "yyyy-MM-dd")
      : "";
    const toStr = rangeValue?.to ? format(rangeValue.to, "yyyy-MM-dd") : "";
    const match = suggestions.find((s) => {
      const sFrom = s.range.from ? format(s.range.from, "yyyy-MM-dd") : "";
      const sTo = s.range.to ? format(s.range.to, "yyyy-MM-dd") : "";
      return sFrom === fromStr && sTo === toStr;
    });
    if (match) return match.label;
    return formatRangeDisplay(rangeValue);
    // Depend on actual dates, not object identity (parent may reuse one reference).
  }, [
    mode,
    direction,
    clinicToday,
    (value as DateRange | undefined)?.from?.getTime(),
    (value as DateRange | undefined)?.to?.getTime(),
  ]);

  // Generate range suggestions for range mode
  const rangeSuggestions = useMemo(() => {
    if (mode === "range") {
      return generateRangeSuggestions(direction, clinicToday);
    }
    return [];
  }, [mode, direction, clinicToday]);

  // Get the single date value for the hook (used for suggestions)
  const getSingleDateValue = (): Date | undefined => {
    if (mode === "range") {
      const rangeValue = value as DateRange | undefined;
      return rangeValue?.from;
    }
    return value as Date | undefined;
  };

  // Check if there's a value set
  const hasValue = (): boolean => {
    if (mode === "range") {
      const rangeValue = value as DateRange | undefined;
      return !!(rangeValue?.from || rangeValue?.to);
    }
    return !!(value as Date | undefined);
  };

  // Handler for single date changes from the hook
  const handleSingleDateChange = (date: Date | undefined) => {
    if (mode === "single" && onChange) {
      (onChange as (date: Date | undefined) => void)(date);
    } else if (mode === "range" && onChange && date) {
      // In range mode, set the suggestion as a single day range
      (onChange as (range: DateRange | undefined) => void)({
        from: date,
        to: date,
      });
    }
  };

  // Custom suggestion select handler that handles the "Custom date..." option
  const handleCustomSuggestionSelect = (suggestion: any) => {
    if (suggestion.id === "custom") {
      // Switch to date picker view within the same popover
      setShowDatePicker(true);
      return;
    }

    // Handle range suggestions
    if (suggestion.isRange && mode === "range" && onChange) {
      (onChange as (range: DateRange | undefined) => void)(suggestion.range);
      setInputValue(formatRangeDisplay(suggestion.range, suggestion.rangeType));
      setOpen(false);
      inputRef.current?.blur();
      return;
    }

    // For regular suggestions, handle them normally
    handleSingleDateChange(suggestion.date);
    setOpen(false);
    inputRef.current?.blur();
  };

  const {
    open,
    setOpen,
    inputValue,
    setInputValue,
    selectedIndex,
    setSelectedIndex,
    suggestions,
    inputRef,
    dropdownRef,
    justOpenedRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleKeyDown,
    parseNaturalLanguage,
    getSuggestionRightText,
  } = useSmartDateInput({
    value: getSingleDateValue(),
    onChange: handleSingleDateChange,
    customDefaultSuggestions: customSuggestions,
    includeCustomOption: true,
    onSuggestionSelect: handleCustomSuggestionSelect,
    clearInputAfterSelection: false, // SmartDateInput shows formatted date
    direction,
    controlledInputDisplay:
      mode === "range" ? rangeControlledInputDisplay : undefined,
    formatParsedDateDisplay:
      mode === "range"
        ? (d) => formatRangeDisplay({ from: d, to: d })
        : undefined,
  });

  // When closed, show the full range from props. The hook only tracks `from` as `value`, so its
  // internal inputValue can still be a single-date string.
  const inputDisplayValue =
    mode === "range" && !open
      ? (rangeControlledInputDisplay ?? "")
      : inputValue;

  const handleRangeAwareFocus = () => {
    if (mode === "range") {
      setInputValue(rangeControlledInputDisplay ?? "");
    }
    handleInputFocus();
  };

  // Auto-focus input on mount if autoFocus is true
  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [autoFocus, inputRef]);

  return (
    <>
      <Popover
        open={disabled ? false : open}
        onOpenChange={(newOpen) => {
          // Don't allow opening if disabled
          if (disabled) {
            return;
          }
          // Prevent immediate closing after opening
          if (!newOpen && justOpenedRef.current) {
            justOpenedRef.current = false;
            return;
          }
          setOpen(newOpen);
          // Reset date picker state when popover closes
          if (!newOpen) {
            setShowDatePicker(false);
          }
        }}
      >
        <PopoverTrigger asChild disabled={disabled}>
          <div
            className="relative"
            onMouseDown={(e) => {
              // Prevent blur when clicking anywhere in the input container
              if (open) {
                e.preventDefault();
              }
            }}
          >
            <Input
              ref={inputRef}
              type="text"
              value={inputDisplayValue}
              onChange={handleInputChange}
              onFocus={handleRangeAwareFocus}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              placeholder={placeholder ?? defaultPlaceholder}
              disabled={disabled}
              className={cx(
                // base
                "relative block w-full appearance-none rounded-md border px-2.5 py-1.5 text-body shadow-sm outline-none transition",
                // border color
                "border-border",
                // text color
                "text-foreground",
                // placeholder color
                "placeholder-foreground-muted",
                // background color
                "bg-background",
                // disabled
                "disabled:border-border disabled:bg-muted disabled:text-foreground-muted",
                // focus
                focusInput,
                // padding adjustment when both buttons are visible
                hasValue() ? "pr-24" : "pr-8",
                className,
              )}
            />
            <ButtonGroup className="absolute right-0 top-0">
              {hasValue() && !disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (mode === "single" && onChange) {
                      (onChange as (date: Date | undefined) => void)(undefined);
                    } else if (mode === "range" && onChange) {
                      (onChange as (range: DateRange | undefined) => void)(
                        undefined,
                      );
                    }
                    setInputValue("");
                    inputRef.current?.focus();
                  }}
                  className="rounded"
                  tabIndex={-1}
                >
                  <RiCloseLine className="h-4 w-4" />
                </Button>
              )}
              <Button
                type="button"
                onClick={(e) => {
                  if (disabled) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  // Open the custom calendar picker directly
                  if (mode === "range") {
                    setInputValue(rangeControlledInputDisplay ?? "");
                  }
                  setShowDatePicker(true);
                  setOpen(true);
                }}
                variant="ghost"
                disabled={disabled}
                className="!rounded-r-md"
                tabIndex={-1}
              >
                <RiCalendarLine className="h-4 w-4" />
              </Button>
            </ButtonGroup>
          </div>
        </PopoverTrigger>

        <PopoverContent
          ref={dropdownRef}
          sideOffset={4}
          side={side}
          align="start"
          avoidCollisions
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cx(
            // base
            "z-50 overflow-hidden py-1",
            showDatePicker
              ? mode === "range"
                ? "w-fit max-w-none p-4"
                : "w-fit p-4"
              : "min-w-[280px] max-w-[320px] p-0",
          )}
        >
          {showDatePicker ? (
            mode === "range" ? (
              <SmartDatePickerPanel
                mode="range"
                value={value as DateRange | undefined}
                direction={direction === "both" ? "future" : direction}
                onChange={(range) => {
                  if (onChange) {
                    (onChange as (range: DateRange | undefined) => void)(range);
                  }
                  if (range) {
                    setInputValue(formatRangeDisplay(range));
                  }
                }}
                onClose={() => {
                  setShowDatePicker(false);
                  setOpen(false);
                  inputRef.current?.blur();
                }}
                parseNaturalLanguage={parseNaturalLanguage}
                autoFocus={false}
              />
            ) : (
              <SmartDatePickerPanel
                mode="single"
                value={value as Date | undefined}
                onChange={(date) => {
                  if (onChange) {
                    (onChange as (date: Date | undefined) => void)(date);
                  }
                  setInputValue(format(date, "MMM d, yyyy"));
                }}
                onClose={() => {
                  setShowDatePicker(false);
                  setOpen(false);
                  inputRef.current?.blur();
                }}
                parseNaturalLanguage={parseNaturalLanguage}
                autoFocus={false}
              />
            )
          ) : mode === "range" ? (
            // Range mode: show range suggestions
            <>
              {rangeSuggestions.map((suggestion, index) => {
                const isSelected = selectedIndex === index;
                const rangeValue = value as DateRange | undefined;
                const isCurrentValue =
                  rangeValue?.from &&
                  rangeValue?.to &&
                  suggestion.range.from &&
                  suggestion.range.to &&
                  format(rangeValue.from, "yyyy-MM-dd") ===
                    format(suggestion.range.from, "yyyy-MM-dd") &&
                  format(rangeValue.to, "yyyy-MM-dd") ===
                    format(suggestion.range.to, "yyyy-MM-dd");

                return (
                  <div
                    key={suggestion.id}
                    className={cx(
                      "flex cursor-pointer items-center justify-between px-3 py-2 outline-none transition-colors",
                      "rounded-md hover:bg-row-hover",
                      isSelected && "bg-hover",
                    )}
                    onClick={() => handleCustomSuggestionSelect(suggestion)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="flex flex-col">
                      <span
                        className={cx(
                          "text-body",
                          isCurrentValue
                            ? "font-medium text-foreground"
                            : "text-foreground-secondary",
                        )}
                      >
                        {suggestion.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-caption text-muted-foreground">
                        {format(suggestion.range.from!, "MMM d")} –{" "}
                        {format(suggestion.range.to!, "MMM d")}
                      </span>
                      {isCurrentValue && (
                        <RiCheckLine className="h-4 w-4 text-foreground-secondary" />
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Custom date range option */}
              <div
                className={cx(
                  "flex cursor-pointer items-center justify-between px-3 py-2 outline-none transition-colors",
                  "mt-1 rounded-md border-t border-border-secondary pt-2 hover:bg-row-hover",
                  selectedIndex === rangeSuggestions.length && "bg-hover",
                )}
                onClick={() => {
                  setShowDatePicker(true);
                }}
                onMouseEnter={() => setSelectedIndex(rangeSuggestions.length)}
              >
                <span className="text-body-strong text-foreground">
                  Custom date range...
                </span>
              </div>
            </>
          ) : suggestions.length === 0 ? (
            <div className="px-3 py-2 text-muted-foreground">
              No matching dates found
            </div>
          ) : (
            // Single mode: show date suggestions
            suggestions.map((suggestion, index) => {
              const isSelected = selectedIndex === index;
              const singleValue = getSingleDateValue();
              const isCurrentValue =
                singleValue &&
                format(singleValue, "yyyy-MM-dd") ===
                  format(suggestion.date, "yyyy-MM-dd");

              return (
                <div
                  key={suggestion.id}
                  className={cx(
                    "flex cursor-pointer items-center justify-between px-3 py-2 outline-none transition-colors",
                    "rounded-md hover:bg-row-hover",
                    isSelected && "bg-hover",
                  )}
                  onClick={() => handleCustomSuggestionSelect(suggestion)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="flex flex-col">
                    <span
                      className={cx(
                        "text-body",
                        suggestion.isCustom
                          ? "font-medium text-foreground"
                          : isCurrentValue
                            ? "font-medium text-foreground"
                            : "text-foreground-secondary",
                      )}
                    >
                      {suggestion.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {suggestion.id !== "custom" && (
                      <span className="text-caption text-muted-foreground">
                        {getSuggestionRightText(suggestion)}
                      </span>
                    )}
                    {isCurrentValue && suggestion.id !== "custom" && (
                      <RiCheckLine className="h-4 w-4 text-foreground-secondary" />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}
